import React from 'react';
import PropTypes from 'prop-types';

const categoryLabels = {
  // Polymet prototype mock keys
  operations: 'Operations',
  financial_health: 'Financial Health',
  human_resources: 'Human Resources',
  customer_concentration: 'Customer Concentration',
  growth_strategy: 'Growth Strategy',
  succession_plan: 'Succession Plan',
  // Production CEPA dimension keys (5-category model — matches the
  // TriggeringEvent payload from real assessment runs in Succeedo).
  financial: 'Financial Capital',
  human: 'Human Capital',
  structural: 'Structural Capital',
  customer: 'Customer Capital',
  wealth: 'Wealth Gap',
};

const getScoreColor = (score) => {
  if (score >= 80) return 'hsl(142, 76%, 36%)';
  if (score >= 60) return 'hsl(var(--succeedo-orange))';
  if (score >= 40) return 'hsl(43, 96%, 56%)';
  return 'hsl(0, 84%, 60%)';
};

const CategoryRadarChart = ({ categoryScores, className }) => {
  const categories = Object.keys(categoryScores || {});
  const scores = Object.values(categoryScores || {});

  if (categories.length === 0) {
    return (
      <div className={`text-[13px] text-[hsl(var(--muted-foreground))] ${className || ''}`}>
        No category scores available.
      </div>
    );
  }

  // viewBox is 540×540 (was 400×400 in Polymet) so longer category labels
  // like "Structural Capital" / "Customer Capital" don't clip at the SVG
  // edges. Chart geometry (maxRadius, labelRadius) is unchanged — we just
  // shift the center inward to give labels breathing room.
  const centerX = 270;
  const centerY = 270;
  const maxRadius = 160;
  const targetRadius = (70 / 100) * maxRadius;
  const angleStep = (2 * Math.PI) / categories.length;

  const scorePoints = scores
    .map((score, i) => {
      const angle = angleStep * i - Math.PI / 2;
      const radius = (score / 100) * maxRadius;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      return `${x},${y}`;
    })
    .join(' ');

  const targetPoints = categories
    .map((_, i) => {
      const angle = angleStep * i - Math.PI / 2;
      const x = centerX + targetRadius * Math.cos(angle);
      const y = centerY + targetRadius * Math.sin(angle);
      return `${x},${y}`;
    })
    .join(' ');

  const axisLines = categories.map((_, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const x = centerX + maxRadius * Math.cos(angle);
    const y = centerY + maxRadius * Math.sin(angle);
    return { x1: centerX, y1: centerY, x2: x, y2: y };
  });

  const labelPositions = categories.map((category, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const labelRadius = maxRadius + 40;
    const x = centerX + labelRadius * Math.cos(angle);
    const y = centerY + labelRadius * Math.sin(angle);
    return {
      x,
      y,
      label: categoryLabels[category] || category,
      score: scores[i],
    };
  });

  return (
    <div className={className || ''}>
      <div className="hidden md:block">
        <svg viewBox="0 0 540 540" className="w-full max-w-[540px] mx-auto" preserveAspectRatio="xMidYMid meet">
          {axisLines.map((line, i) => (
            <line
              key={`axis-${categories[i]}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke="hsl(var(--border))"
              strokeWidth="1"
            />
          ))}
          <polygon
            points={targetPoints}
            fill="none"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth="1"
            strokeDasharray="4 4"
            opacity="0.3"
          />
          <polygon
            points={scorePoints}
            fill="hsl(var(--succeedo-orange))"
            fillOpacity="0.15"
            stroke="hsl(var(--succeedo-orange))"
            strokeWidth="2"
          />
          {scores.map((score, i) => {
            const angle = angleStep * i - Math.PI / 2;
            const radius = (score / 100) * maxRadius;
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            return (
              <circle
                key={`point-${categories[i]}`}
                cx={x}
                cy={y}
                r="4"
                fill={getScoreColor(score)}
              />
            );
          })}
          {labelPositions.map((pos) => (
            <g key={`label-${pos.label}`}>
              <text
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-[11px] font-medium fill-[hsl(var(--foreground))]">
                {pos.label}
              </text>
              <text
                x={pos.x}
                y={pos.y + 14}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-[13px] font-semibold"
                fill={getScoreColor(pos.score)}>
                {pos.score}
              </text>
            </g>
          ))}
        </svg>

        <div className="flex items-center justify-center gap-6 mt-6 text-[12px] text-[hsl(var(--muted-foreground))]">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            <span>Your Score</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full border border-dashed border-[hsl(var(--muted-foreground))]" />
            <span>Target (70)</span>
          </div>
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {categories.map((category) => {
          const score = categoryScores[category];
          return (
            <div key={category}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[hsl(var(--foreground))]">
                  {categoryLabels[category] || category}
                </span>
                <span className="text-[14px] font-semibold" style={{ color: getScoreColor(score) }}>
                  {score}
                </span>
              </div>
              <div className="h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${score}%`,
                    backgroundColor: getScoreColor(score),
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

CategoryRadarChart.propTypes = {
  categoryScores: PropTypes.objectOf(PropTypes.number).isRequired,
  className: PropTypes.string,
};

CategoryRadarChart.defaultProps = {
  className: '',
};

export default CategoryRadarChart;
